import React, { useEffect, useState, useRef } from "react";
import { collection, getDocs, query } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { CHAPTER_CENTERS, CHAPTER_NAMES } from "../../data/chapterConfig";
import { makeGeocoder } from "../../helpers/pitchGeocoder";
import "./FounderMap.css";

function getQuarterFromTimestamp(timestamp) {
  try {
    const date = timestamp?.toDate?.() || new Date(timestamp);
    const q = Math.floor(date.getMonth() / 3) + 1;
    return `Q${q} ${date.getFullYear()}`;
  } catch {
    return "N/A";
  }
}

export default function FounderMap({ onClose, windowId, bringToFront, isEmbedded = false }) {
  const [founders, setFounders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [chapterFilter, setChapterFilter] = useState("");
  const [search, setSearch] = useState("");
  const [mapCenter, setMapCenter] = useState({
    lat: 39.5,
    lng: -98.35,
    zoom: 4,
  });
  const [selectedFounder, setSelectedFounder] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [zipcodeData, setZipcodeData] = useState(null);
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const markerMapRef = useRef(new Map()); // Map founder ID to marker
  
  // Force initialization function (exposed for debugging)
  const forceInitMap = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
    setMapReady(false);
    setTimeout(() => {
      const container = mapRef.current;
      if (!container || !window.L) {
        console.error("Cannot force init - missing container or Leaflet");
        return;
      }
      try {
        const map = window.L.map(container, {
          center: [mapCenter.lat, mapCenter.lng],
          zoom: mapCenter.zoom
        });
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);
        mapInstanceRef.current = map;
        setMapReady(true);
        map.invalidateSize();
      } catch (err) {
        console.error("Force init failed:", err);
      }
    }, 100);
  };
  
  // Expose for debugging
  useEffect(() => {
    window._debugForceInitMap = forceInitMap;
    return () => {
      delete window._debugForceInitMap;
    };
  }, []);

  // Load zipcode data
  useEffect(() => {
    fetch('/data/zipcodes.json')
      .then(res => res.json())
      .then(data => {
        setZipcodeData(data);
      })
      .catch(err => {
        console.error('Failed to load zipcode data:', err);
        setError('Failed to load location data');
      });
  }, []);

  // Force initialization when component mounts and container is ready
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;
    
    const checkLeafletAndInit = () => {
      attempts++;

      if (!window.L) {
        console.warn("Leaflet not yet available, waiting...");
        if (attempts < maxAttempts) {
          setTimeout(checkLeafletAndInit, 500);
        } else {
          console.error("Leaflet failed to load after 10 attempts");
          setError("Unable to load map library. Please refresh the page.");
          setLoading(false);
        }
        return;
      }

      const leafletCSSId = 'leaflet-css-check';
      if (!document.getElementById(leafletCSSId)) {
        const link = document.createElement('link');
        link.id = leafletCSSId;
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        link.crossOrigin = '';
        document.head.appendChild(link);
      }

      const waitForContainer = () => {
        if (!mapRef.current) {
          setTimeout(waitForContainer, 100);
          return;
        }

        initializeMap();
      };
      
      waitForContainer();
    };
    
    const initializeMap = () => {
      if (mapInstanceRef.current) {
        setMapReady(true);
        return;
      }
      
      const container = mapRef.current;
      if (!container) {
        console.error("Container lost reference");
        return;
      }
      
      const rect = container.getBoundingClientRect();

      if (rect.width === 0 || rect.height === 0) {
        console.warn("Container has zero dimensions, retrying...");
        setTimeout(initializeMap, 200);
        return;
      }
      
      try {
        if (container._leaflet_id) {
          delete container._leaflet_id;
        }

        const map = window.L.map(container, {
          center: [mapCenter.lat, mapCenter.lng],
          zoom: mapCenter.zoom,
          zoomControl: true,
          attributionControl: true,
          preferCanvas: true // Better performance
        });

        const tileLayer = window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 19,
          crossOrigin: true
        });
        
        tileLayer.addTo(map);

        mapInstanceRef.current = map;
        setMapReady(true);
        
        // Force a proper resize
        setTimeout(() => {
          map.invalidateSize(true);
          map.setView([mapCenter.lat, mapCenter.lng], mapCenter.zoom);
        }, 100);

      } catch (err) {
        console.error("Failed to initialize map:", err);
        setError(`Map initialization failed: ${err.message}`);
        setLoading(false);
      }
    };
    
    checkLeafletAndInit();

    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
          setMapReady(false);
        } catch (err) {
          console.error("Error removing map:", err);
        }
      }
    };
  }, []); // Empty deps to run once on mount

  // Update map view when center changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([mapCenter.lat, mapCenter.lng], mapCenter.zoom);
    }
  }, [mapCenter]);

  // Add markers when locations change OR map becomes ready
  useEffect(() => {
    if (!mapInstanceRef.current || !mapReady) {
      return;
    }

    if (locations.length === 0) {
      return;
    }

    // Small delay to ensure map is fully rendered
    const timeoutId = setTimeout(() => {
      markersRef.current.forEach(marker => {
        mapInstanceRef.current.removeLayer(marker);
      });
      markersRef.current = [];
      markerMapRef.current.clear();

      let addedCount = 0;

      locations.forEach((founder) => {
        if (!founder.coords?.lat || !founder.coords?.lng) {
          return;
        }

        try {
          const marker = window.L.marker([founder.coords.lat, founder.coords.lng])
            .addTo(mapInstanceRef.current);

          const popupContent = `
            <div style="font-size: 14px; max-width: 280px;">
              ${founder.photo ? `
                <div style="text-align: center; margin-bottom: 8px;">
                  <img class="founder-popup-photo" src="${founder.photo}" style="width: 200px; height: 150px; object-fit: cover; border-radius: 4px; cursor: zoom-in;" title="Click to enlarge" />
                </div>
              ` : ''}
              <div style="font-weight: bold; margin-bottom: 6px; font-size: 16px;">${founder.businessName || 'Business'}</div>
              <div style="margin-bottom: 4px;"><strong>Founder:</strong> ${founder.founderName || 'Unknown'}</div>
              <div style="margin-bottom: 4px;"><strong>Quarter:</strong> ${founder.quarter || 'N/A'}</div>
              ${founder.chapter ? `<div style="margin-bottom: 4px;"><strong>Chapter:</strong> ${founder.chapter}</div>` : ''}
              ${founder.about ? `
                <div style="margin: 8px 0; padding: 8px; background-color: #f5f5f5; border-radius: 4px; font-size: 13px;">
                  <strong>About:</strong> ${founder.about}
                </div>
              ` : ''}
              ${founder.website ? `
                <div style="margin-top: 8px;">
                  <a href="${founder.website.startsWith('http') ? founder.website : `https://${founder.website}`}" target="_blank" style="color: #0066cc; text-decoration: none;">
                    Visit Website →
                  </a>
                </div>
              ` : ''}
            </div>
          `;

          marker.bindPopup(popupContent);

          marker.on('click', () => {
            setSelectedFounder(founder);
          });

          if (founder.photo) {
            marker.on('popupopen', (e) => {
              const img = e.popup.getElement()?.querySelector('.founder-popup-photo');
              if (img && !img.dataset.enlargeBound) {
                img.dataset.enlargeBound = 'true';
                img.addEventListener('click', (ev) => {
                  ev.stopPropagation();
                  setExpandedImage(founder.photo);
                });
              }
            });
          }

          markersRef.current.push(marker);
          markerMapRef.current.set(founder.id, marker);
          addedCount++;
        } catch (err) {
          console.error(`Failed to add marker for ${founder.businessName}:`, err);
        }
      });

      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize();
      }
    }, 200); // Small delay to ensure map is ready

    return () => clearTimeout(timeoutId);
  }, [locations, mapReady]); // Add mapReady as dependency

  // Fetch founders data
  useEffect(() => {
    const fetchFounders = async () => {
      // Wait for zipcode data to load
      if (!zipcodeData) {
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // Public projection of published winners only — `pitches` is
        // reviewer-gated; winners are mirrored to publicWinners server-side.
        const q = query(collection(db, "publicWinners"));
        const snap = await getDocs(q);
        // Shared geocoder: precise zip where available, chapter-center fallback
        // otherwise, so winners from every chapter land on the map.
        const geocode = makeGeocoder(zipcodeData);
        const data = [];

        let skippedCount = 0;

        snap.docs.forEach((doc) => {
          const d = doc.data();
          // Hide winners staged in the admin Grant awards tab but not yet
          // published. Missing field reads as published (existing winners).
          if (d.winnerPublished === false) {
            skippedCount++;
            return;
          }

          // Pass the doc id in so same-zip winners get distinct fallback spots
          // (doc.data() alone has no id).
          const coords = geocode({ ...d, id: doc.id });
          if (!coords) {
            skippedCount++;
            return;
          }

          data.push({
            ...d,
            id: doc.id,
            coords: { lat: coords.lat, lng: coords.lng },
            approximate: coords.approximate,
            zipCode: d.zipCode || d.zip || d["zip-code"] || d.postalCode,
            quarter: getQuarterFromTimestamp(d.createdAt),
            photo: d["pitch-photo"] || d.photo,
            businessName: d.businessName || d["business-name"],
            founderName: d.founderName || d["founder-name"]
          });
        });

        data.sort((a, b) =>
          (a.businessName || "").localeCompare(b.businessName || "")
        );

        setFounders(data);
        setLocations(data);
      } catch (err) {
        console.error("Error fetching founders:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchFounders();
  }, [zipcodeData]); // Re-run when zipcode data loads

  const handleChapterChange = (e) => {
    const chapter = e.target.value;
    setChapterFilter(chapter);
    if (chapter && CHAPTER_CENTERS[chapter]) {
      setMapCenter(CHAPTER_CENTERS[chapter]);
      setLocations(founders.filter((f) => f.chapter === chapter));
    } else {
      setMapCenter({ lat: 39.5, lng: -98.35, zoom: 4 });
      setLocations(founders);
    }
  };

  const handleSearch = (e) => {
    const val = e.target.value.toLowerCase();
    setSearch(val);
    setLocations(
      founders.filter((f) => {
        const matchesSearch =
          f.businessName?.toLowerCase().includes(val) ||
          f.founderName?.toLowerCase().includes(val);
        const matchesChapter = !chapterFilter || f.chapter === chapterFilter;
        return matchesSearch && matchesChapter;
      })
    );
  };

  const handleBusinessClick = (founder) => {
    setSelectedFounder(founder);

    if (mapInstanceRef.current && founder.coords) {
      mapInstanceRef.current.setView([founder.coords.lat, founder.coords.lng], 14);

      const marker = markerMapRef.current.get(founder.id);
      if (marker) {
        // Small delay to allow map to center first
        setTimeout(() => {
          marker.openPopup();
          // Pan the map up slightly so popup is fully visible
          setTimeout(() => {
            mapInstanceRef.current.panBy([0, -100]);
          }, 100);
        }, 300);
      }
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
        Loading map data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
        <div style={{ textAlign: "center", color: "red" }}>
          <div>Error: {error}</div>
          <button onClick={() => window.location.reload()} style={{ marginTop: "10px" }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fm-layout" style={{ display: "flex", height: "100%", width: "100%" }}>
      {/* Sidebar */}
      <div className="fm-sidebar" style={{
        width: 250,
        borderRight: "1px solid #aaa",
        overflowY: "auto",
        padding: 10,
        backgroundColor: "#f9f9f9",
        flexShrink: 0
      }}>
        <h3 style={{
          margin: 0,
          fontSize: 16,
          borderBottom: "1px solid #ccc",
          paddingBottom: 8,
        }}>
          Founders ({locations.length})
        </h3>
        <div style={{ overflowY: "auto", maxHeight: "calc(100% - 40px)" }}>
          {locations.map((founder) => (
            <div
              key={founder.id}
              onClick={() => handleBusinessClick(founder)}
              style={{
                padding: 8,
                margin: "5px 0",
                border: "1px solid #ddd",
                cursor: "pointer",
                backgroundColor: selectedFounder?.id === founder.id ? "#e3f2fd" : "var(--gnf-bg)",
              }}
            >
              <div style={{ fontWeight: "bold", fontSize: 14 }}>
                {founder.businessName}
              </div>
              <div style={{ fontSize: 12, color: "#666" }}>
                {founder.founderName}
              </div>
              <div style={{ fontSize: 12, color: "var(--gnf-text-muted)" }}>
                {founder.chapter}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Map + Controls */}
      <div style={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        padding: 10,
        height: "100%",
        minWidth: 0
      }}>
        <div className="mb-form-shell" style={{ marginBottom: 10, display: "flex", gap: 10 }}>
          <input
            type="text"
            placeholder="Search founder or business"
            value={search}
            onChange={handleSearch}
            style={{ flexGrow: 1 }}
          />
          <select
            value={chapterFilter}
            onChange={handleChapterChange}
          >
            <option value="">All Chapters</option>
            {CHAPTER_NAMES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div
          ref={mapRef}
          style={{
            flexGrow: 1,
            border: "2px solid #aaa",
            minHeight: "400px",
            backgroundColor: "var(--gnf-bg-gray)",
            backgroundImage: "repeating-linear-gradient(45deg, var(--gnf-bg-silver) 0px, var(--gnf-bg-silver) 10px, var(--gnf-bg-gray) 10px, var(--gnf-bg-gray) 20px)",
            position: "relative"
          }}
        >
          {!mapReady && (
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "#666",
              fontSize: "14px",
              textAlign: "center",
              padding: "20px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}>
              <div style={{ marginBottom: "10px" }}>🗺️</div>
              Map Container Ready<br/>
              <small>Initializing map...</small>
              <div style={{ marginTop: "10px", fontSize: "12px" }}>
                Check console for detailed logs
              </div>
              <button 
                onClick={() => window.location.reload()} 
                style={{
                  marginTop: "15px",
                  padding: "5px 15px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "12px"
                }}
              >
                Refresh Map
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {expandedImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
          onClick={() => setExpandedImage(null)}
        >
          <img 
            src={expandedImage} 
            alt="Expanded view" 
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain'
            }}
          />
        </div>
      )}
    </div>
  );
}